(function () {
  'use strict';

  if (customElements.get('dream-product')) return;

  class DreamProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.quantityOptions = Array.from(this.querySelectorAll('[data-quantity-option]'));
      this.purchaseOptions = Array.from(this.querySelectorAll('[data-purchase-option]'));
      this.plans = Array.from(this.querySelectorAll('[data-dream-plan]'));
      this.subscriptionOption = this.querySelector('[data-subscription-option]');
      this.form = this.querySelector('[data-dream-form]');
      this.status = this.querySelector('[data-form-status]');
      this.addButton = this.querySelector('[data-add-button]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.mobileGalleryQuery = window.matchMedia('(max-width: 620px)');
      this.activeSlide = 0;

      this.bindGallery();
      this.bindLightbox();
      if (this.slides.length) this.showSlide(0);
      this.bindOptions();
      this.bindForm();
      this.applyPackIntent();
      if (this.subscriptionOption && this.plans.some((plan) => Number(plan.dataset.quantity) === this.selectedQuantity())) {
        this.subscriptionOption.checked = true;
      }
      this.updatePurchasePrices();
    }

    bindGallery() {
      if (this.slides.length < 2) {
        const controls = this.querySelector('[data-gallery-controls]');
        if (controls) controls.hidden = true;
        return;
      }

      const previous = this.querySelector('[data-gallery-previous]');
      const next = this.querySelector('[data-gallery-next]');
      if (previous) previous.addEventListener('click', () => this.showSlide(this.activeSlide - 1));
      if (next) next.addEventListener('click', () => this.showSlide(this.activeSlide + 1));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.galleryThumb)));
      });
      this.querySelector('[data-gallery-facts]')?.addEventListener('click', async () => {
        await this.showSlide(this.slides.length - 1);
        if (this.mobileGalleryQuery.matches) {
          this.openLightbox();
        } else {
          this.querySelector('[data-gallery-stage]')?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      });
    }

    bindLightbox() {
      const stage = this.querySelector('[data-gallery-stage]');
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      if (!stage || !trigger || !this.lightbox || !this.lightboxImage || typeof this.lightbox.showModal !== 'function') return;

      stage.addEventListener('click', (event) => {
        if (event.target.closest('button') || !event.target.closest('[data-gallery-slide]')) return;
        this.openLightbox();
      });
      trigger.addEventListener('click', () => this.openLightbox());
      this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
      this.querySelector('[data-lightbox-previous]')?.addEventListener('click', () => this.showSlide(this.activeSlide - 1));
      this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.showSlide(this.activeSlide + 1));
      this.lightbox.addEventListener('click', (event) => {
        if (!event.target.closest('button') && event.target !== this.lightboxImage) this.closeLightbox();
      });
      this.lightbox.addEventListener('close', () => this.finishLightboxClose());
      this.handleGalleryBreakpoint = (event) => {
        if (!event.matches) this.closeLightbox();
      };
      this.mobileGalleryQuery.addEventListener('change', this.handleGalleryBreakpoint);
    }

    openLightbox() {
      if (!this.mobileGalleryQuery.matches || !this.lightbox || this.lightbox.open) return;
      this.lightboxReturnFocus = this.querySelector('[data-gallery-lightbox-trigger]');
      this.updateLightboxImage(false);
      this.lockLightboxScroll();
      this.lightbox.showModal();
      this.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
    }

    closeLightbox() {
      if (this.lightbox?.open) this.lightbox.close();
    }

    finishLightboxClose() {
      if (this.lightboxScrollLocked) {
        if (this.lightboxUsesSharedLock) {
          window.Wisp.unlockScroll();
        } else {
          document.body.style.overflow = this.previousBodyOverflow || '';
        }
        this.lightboxScrollLocked = false;
        this.lightboxUsesSharedLock = false;
      }
      this.lightboxReturnFocus?.focus({ preventScroll: true });
      this.lightboxReturnFocus = null;
    }

    lockLightboxScroll() {
      if (this.lightboxScrollLocked) return;
      this.previousBodyOverflow = document.body.style.overflow;
      this.lightboxUsesSharedLock = Boolean(window.Wisp && typeof window.Wisp.lockScroll === 'function');
      if (this.lightboxUsesSharedLock) {
        window.Wisp.lockScroll();
      } else {
        document.body.style.overflow = 'hidden';
      }
      this.lightboxScrollLocked = true;
    }

    updateLightboxImage(announce = true) {
      if (!this.lightboxImage) return;
      const sourceImage = this.slides[this.activeSlide]?.querySelector('img');
      if (!sourceImage) return;

      const request = (this.lightboxImageRequest || 0) + 1;
      const media = this.querySelector('[data-lightbox-media]');
      const source = sourceImage.dataset.lightboxSrc || sourceImage.currentSrc || sourceImage.getAttribute('src');
      this.lightboxImageRequest = request;
      media?.setAttribute('aria-busy', 'true');
      this.lightboxImage.alt = sourceImage.alt;
      this.lightboxImage.src = source;
      const count = this.querySelector('[data-lightbox-count]');
      const caption = this.querySelector('[data-lightbox-caption]');
      const status = this.querySelector('[data-lightbox-status]');
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      const position = this.activeSlide + 1;
      if (count) count.textContent = position + ' / ' + this.slides.length;
      if (caption) caption.textContent = sourceImage.alt;
      if (trigger) trigger.setAttribute('aria-label', 'Open image ' + position + ' of ' + this.slides.length + ' fullscreen');
      if (status && announce) status.textContent = 'Image ' + position + ' of ' + this.slides.length;

      const loaded = this.lightboxImage.decode ? this.lightboxImage.decode() : Promise.resolve();
      loaded.catch(() => {}).finally(() => {
        if (request === this.lightboxImageRequest) media?.removeAttribute('aria-busy');
      });
    }

    async showSlide(index) {
      const nextSlide = (index + this.slides.length) % this.slides.length;
      const request = (this.slideRequest || 0) + 1;
      const incomingSlide = this.slides[nextSlide];
      const incomingImage = incomingSlide?.querySelector('img');
      const stage = this.querySelector('[data-gallery-stage]');
      this.slideRequest = request;

      if (incomingImage && (!incomingImage.complete || !incomingImage.naturalWidth)) {
        stage?.setAttribute('aria-busy', 'true');
        incomingImage.loading = 'eager';
        try {
          await incomingImage.decode();
        } catch (error) {}
        if (request !== this.slideRequest) return;
      }

      stage?.removeAttribute('aria-busy');
      this.activeSlide = nextSlide;
      this.slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== this.activeSlide; });
      this.thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute('aria-pressed', String(thumbIndex === this.activeSlide));
      });
      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = 'Image ' + (this.activeSlide + 1) + ' of ' + this.slides.length;
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      if (trigger) trigger.setAttribute('aria-label', 'Open image ' + (this.activeSlide + 1) + ' of ' + this.slides.length + ' fullscreen');
      if (this.lightbox?.open) this.updateLightboxImage();
    }

    bindOptions() {
      this.quantityOptions.forEach((option) => {
        option.addEventListener('change', () => this.updatePurchasePrices());
      });
      this.purchaseOptions.forEach((option) => {
        option.addEventListener('change', () => this.updatePurchasePrices());
      });
    }

    selectedQuantity() {
      const selected = this.quantityOptions.find((option) => option.checked);
      return selected ? Number(selected.value) : 3;
    }

    applyPackIntent() {
      const pack = Number(new URL(window.location.href).searchParams.get('pack'));
      if (![1, 2, 3].includes(pack)) return;
      const option = this.quantityOptions.find((candidate) => Number(candidate.value) === pack);
      if (option) option.checked = true;
    }

    selectedPurchase() {
      return this.purchaseOptions.find((option) => option.checked);
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      }).format(Number(cents) / 100);
    }

    updatePurchasePrices() {
      const quantity = this.selectedQuantity();
      const quantityOption = this.quantityOptions.find((option) => option.checked);
      const onceTotal = Number(quantityOption.dataset.onceTotal);
      const plan = this.plans.find((candidate) => Number(candidate.dataset.quantity) === quantity);
      const subscriptionOption = this.subscriptionOption;
      const setText = (selector, value) => {
        const element = this.querySelector(selector);
        if (element) element.textContent = value;
      };

      subscriptionOption.disabled = !plan;
      if (!plan && subscriptionOption.checked) {
        this.purchaseOptions.find((option) => option !== subscriptionOption).checked = true;
      }
      subscriptionOption.value = plan?.dataset.id || '';
      subscriptionOption.dataset.sellingPlanId = plan?.dataset.id || '';
      subscriptionOption.dataset.planName = plan?.dataset.name || '';
      subscriptionOption.dataset.planCadence = plan?.dataset.cadence || '';
      subscriptionOption.dataset.planRenewalEstimated = plan?.dataset.renewalEstimated || 'false';
      this.plans.forEach((candidate) => {
        subscriptionOption.setAttribute('data-plan-total-' + candidate.dataset.quantity, candidate.dataset.total);
        subscriptionOption.setAttribute('data-plan-renewal-total-' + candidate.dataset.quantity, candidate.dataset.renewal);
      });
      setText('[data-once-price]', this.money(onceTotal));
      setText('[data-plan-price]', plan ? this.money(plan.dataset.total) : '');
      const planSaving = this.querySelector('[data-plan-saving]');
      if (planSaving) {
        const extraSavings = plan && onceTotal > 0 ? Math.max(0, Math.round((onceTotal - Number(plan.dataset.total)) * 100 / onceTotal)) : 0;
        planSaving.hidden = extraSavings <= 0;
        planSaving.textContent = 'Save ' + extraSavings + '% on your first delivery';
      }
      const tins = (count) => count + (count === 1 ? ' tin' : ' tins');
      const setHidden = (selector, hidden) => {
        const element = this.querySelector(selector);
        if (element) element.hidden = hidden;
      };
      setText('[data-plan-description]', plan
        ? tins(quantity) + ' ' + plan.dataset.cadence
        : 'Subscription temporarily unavailable');
      this.querySelector('[data-subscription-choice]')?.classList.toggle('is-unavailable', !plan);

      const subscribed = subscriptionOption.checked && Boolean(plan);
      const selectedTotal = subscribed ? Number(plan.dataset.total) : onceTotal;
      setHidden('[data-once-total-summary]', subscribed);
      setHidden('[data-subscription-totals]', !subscribed);
      setHidden('[data-subscription-details]', !subscribed);
      setText('[data-selected-quantity]', tins(quantity) + ' · ' + quantity * 30 + ' strips');
      setText('[data-selected-once-total]', this.money(onceTotal));
      if (subscribed) {
        const renewalEstimated = plan.dataset.renewalEstimated === 'true';
        setText('[data-renewal-label]', renewalEstimated ? 'Renews at est.' : 'Renews at');
        const renewalNote = this.querySelector('[data-bundle-renewal-note]');
        if (renewalNote) renewalNote.hidden = !renewalEstimated;
        setText('[data-subscription-first-total]', this.money(plan.dataset.total));
        setText('[data-subscription-renewal-total]', this.money(plan.dataset.renewal));
        setText('[data-subscription-cadence]', plan.dataset.cadence);
      }
      // Each tin card shows the price of the purchase type that is selected (subscription allocation or one-time bundle),
      // its price per strip (30 strips per tin) and the saving against buying single tins at list price.
      this.quantityOptions.forEach((option) => {
        const packQuantity = Number(option.value);
        const packPlan = this.plans.find((candidate) => Number(candidate.dataset.quantity) === packQuantity);
        const total = subscribed && packPlan ? Number(packPlan.dataset.total) : Number(option.dataset.onceTotal);
        const compare = Number(option.dataset.onceCompare);
        const card = option.closest('label');
        const totalElement = card.querySelector('[data-quantity-total]');
        const unitElement = card.querySelector('[data-quantity-unit-price]');
        const saving = card.querySelector('[data-quantity-discount]');
        if (totalElement) totalElement.textContent = this.money(total);
        if (unitElement) unitElement.textContent = this.money(Math.round(total / (packQuantity * 30)));
        if (saving) {
          saving.hidden = compare <= total;
          saving.textContent = 'Save ' + this.money(compare - total);
        }
      });
      setText('[data-add-price]', this.money(selectedTotal));
      this.dataset.selectedTotal = String(selectedTotal);
      this.dispatchEvent(new CustomEvent('pdp:purchase-change', { bubbles: true }));
    }

    bindForm() {
      if (!this.form || !this.addButton || !window.fetch || !window.FormData) return;
      this.form.addEventListener('submit', (event) => this.addToCart(event));
    }


    async addToCart(event) {
      event.preventDefault();
      if (this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;

      const formData = new FormData(this.form);
      const label = this.querySelector('[data-add-label]');
      const originalLabel = label ? label.textContent : '';
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
      this.quantityOptions.forEach((option) => { option.disabled = true; });
      this.purchaseOptions.forEach((option) => { option.disabled = true; });
      if (label) label.textContent = 'ADDING...';
      this.setStatus('Adding your Dream strips to cart.', 'status');

      try {
        await window.FuseCartQuantity.add(this.cartAddUrl(), formData);

        this.setStatus('Dream strips added to cart.', 'success');
        if (label) label.textContent = 'ADDED';
        if (window.Wisp) {
          if (typeof window.Wisp.showToast === 'function') window.Wisp.showToast('Dream added to cart');
          if (typeof window.Wisp.openCart === 'function') window.Wisp.openCart();
        }
      } catch (error) {
        if (error.addConfirmed) {
          this.setStatus('Dream added. Open your cart to finish updating subscription pricing.', 'status');
          if (label) label.textContent = 'ADDED';
          window.Wisp?.openCart?.();
          return;
        }
        this.setStatus(error.transportError ? 'The cart request could not be confirmed. Check your cart before retrying.' : error.message, 'error');
        if (label) label.textContent = 'TRY AGAIN';
        if (window.Wisp && typeof window.Wisp.showToast === 'function') window.Wisp.showToast('Could not add to cart');
      } finally {
        window.setTimeout(() => {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = this.addButton.dataset.productAvailable === 'false';
          this.quantityOptions.forEach((option) => { option.disabled = false; });
          this.purchaseOptions.forEach((option) => { option.disabled = false; });
          this.updatePurchasePrices();
          if (label) label.textContent = originalLabel;
        }, 1200);
      }
    }

    cartAddUrl() {
      const url = this.dataset.cartAddUrl || '/cart/add';
      return url.endsWith('.js') ? url : url + '.js';
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.state = state;
    }
  }

  customElements.define('dream-product', DreamProduct);
})();
